import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ArrowDown, ArrowUp, ChevronsUpDown, Filter } from 'lucide-react';

/**
 * Pieces shared by the Modified Duty table pages (Modified Duty, Sick Hours): the
 * sortable / filterable header cell, the fixed filter-popover shell, the fixed
 * tooltip, the toast timer and the blob download. Styles are the .md-* classes in
 * ModifiedDutyPage.css — a page that uses these imports that stylesheet.
 *
 * Everything that overlays (popover, tooltip, toast) is position: fixed, so opening
 * or closing it never moves the table.
 */

/* ====================================================================== *
 * Header cell — sort button + optional filter button.
 * ====================================================================== */

export const MdTh: React.FC<{
  label: string;
  /** Used in the sort button's title when the visible label is abbreviated. */
  fullLabel?: string;
  num?: boolean;
  sorted: boolean;
  dir: 'asc' | 'desc';
  filtered?: boolean;
  popOpen?: boolean;
  onSort: () => void;
  /** Omit for a sort-only column. */
  onFilter?: (btn: HTMLButtonElement) => void;
  className?: string;
  rowSpan?: number;
}> = ({ label, fullLabel, num, sorted, dir, filtered, popOpen, onSort, onFilter, className, rowSpan }) => {
  const SortIc = sorted ? (dir === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <th
      rowSpan={rowSpan}
      className={`md-th${num ? ' num' : ''}${sorted ? ' sorted' : ''}${filtered ? ' filtered' : ''}${popOpen ? ' popopen' : ''}${className ? ` ${className}` : ''}`}
      aria-sort={sorted ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="md-thc">
        <button type="button" className="md-sortbtn" onClick={onSort} title={`Sort by ${fullLabel ?? label}`}>
          <span className="md-lbl">{label}</span>
          <span className="md-sic"><SortIc /></span>
        </button>
        {onFilter && (
          <button
            type="button"
            className="md-fbtn"
            aria-label={`Filter ${label}`}
            aria-expanded={!!popOpen}
            onClick={(e) => onFilter(e.currentTarget)}
          >
            <Filter />
          </button>
        )}
      </div>
    </th>
  );
};

/* ====================================================================== *
 * Filter popover shell — portaled, fixed, anchored below the header cell.
 * ====================================================================== */

export const MdPopover: React.FC<{
  anchor: HTMLElement;
  title: string;
  wide?: boolean;
  /** Changes when the popover switches column — refocuses the first text input. */
  focusKey: string;
  canClear: boolean;
  onClear: () => void;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ anchor, title, wide, focusKey, canClear, onClear, onClose, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    let left = r.left;
    if (left + w > window.innerWidth - 12) left = Math.max(12, r.right - w);
    setPos({ left, top: r.bottom + 6 });
  }, [anchor]);

  useLayoutEffect(() => { place(); }, [place, focusKey]);

  useEffect(() => {
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [place]);

  // Focus the first text input when the popover opens (not on every keystroke).
  useEffect(() => {
    const first = ref.current?.querySelector<HTMLInputElement>('input:not([type="checkbox"])');
    first?.focus({ preventScroll: true });
  }, [focusKey]);

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className={`md-pop${wide ? ' wide' : ''}`}
      role="dialog"
      aria-label={`Filter ${title}`}
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
    >
      <div className="md-pop-h"><Filter />{title}</div>
      <div className="md-pop-b">{children}</div>
      <div className="md-pop-f">
        <button type="button" className="md-linkbtn" disabled={!canClear} onClick={onClear}>
          Clear
        </button>
        <span className="grow" />
        <button type="button" className="md-btn sm" onClick={onClose}>Done</button>
      </div>
    </div>,
    document.body,
  );
};

/** Closes an open popover on an outside mousedown (the popover and filter buttons excepted) or Escape. */
export function usePopoverDismiss(open: boolean, close: () => void): void {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (t?.closest('.md-pop') || t?.closest('.md-fbtn')) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
}

/* ====================================================================== *
 * Tooltip — portaled, fixed; below the anchor rect, or above if it won't fit.
 * Pass a new rect object to re-place it.
 * ====================================================================== */

export const MdTip: React.FC<{ rect: DOMRect; className?: string; children: React.ReactNode }> = ({ rect, className, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - el.offsetWidth - 12));
    let top = rect.bottom + 6;
    if (top + el.offsetHeight > window.innerHeight - 12) top = rect.top - el.offsetHeight - 6;
    setPos({ left, top });
    // Keyed on the rect only: each show passes a fresh rect, and JSX children are a new
    // object every render (depending on them would re-place forever).
  }, [rect]);
  return ReactDOM.createPortal(
    <div
      ref={ref}
      className={`md-tip${className ? ` ${className}` : ''}`}
      role="tooltip"
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  );
};

/** Hides a tooltip on any scroll or resize (its rect would be stale). */
export function useHideOnScroll(active: boolean, hide: () => void): void {
  useEffect(() => {
    if (!active) return;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [active, hide]);
}

/* ====================================================================== *
 * Toast + download
 * ====================================================================== */

export const TOAST_MS = 3400;

/** The page renders `<div className={`md-toast${toastOn ? ' on' : ''}`}>{toast}</div>`. */
export function useMdToast(): {
  toast: React.ReactNode;
  toastOn: boolean;
  showToast: (node: React.ReactNode, ms?: number) => void;
} {
  const [toast, setToast] = useState<React.ReactNode>(null);
  const [toastOn, setToastOn] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const showToast = useCallback((node: React.ReactNode, ms: number = TOAST_MS) => {
    setToast(node);
    setToastOn(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToastOn(false), ms);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return { toast, toastOn, showToast };
}

/** Saves a blob through a temporary <a download>. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
